/**
 * findingClusters — merge the SAME claim stated by different analyses,
 * WITHOUT inventing prose and WITHOUT erasing disagreement.
 *
 * ── The defect this replaces ─────────────────────────────────────────
 *
 * Every cross-report surface (the Findings dossier, the Decision Guide
 * dossier, the executive-summary conclusion builder) deduplicated its
 * findings by exact normalised string equality. Two analyses have to
 * write character-identical titles to merge, and they never do. Live on
 * `site-1780556191149-rab796` the Key findings lane rendered 48 rows for
 * ~26 distinct claims — five separate rows for one power deficit, four
 * for one rezoning requirement, four for one floodplain — under a caption
 * that promised "Repeated conclusions are merged".
 *
 * ── Why this is not a similarity metric ──────────────────────────────
 *
 * Two things were hiding inside those duplicate stacks, and a naive
 * "collapse things that look alike" pass would have deleted both:
 *
 *   1. QUANTITY DISAGREEMENT. Four analyses sized the deficit at 500 MW
 *      (550 MW facility − 50 MW indicated); the feasibility analysis sized
 *      it at 250 MW because it was built on a 300 MW requirement. The
 *      analyses disagree about the project's own size. Picking a winner
 *      would have made that disagreement unrecoverable.
 *
 *   2. POLARITY DISAGREEMENT. WRI Aqueduct 1.53/5 is stated as
 *      "Low baseline water stress … favorable" by the water analysis and
 *      as "Water stress · Moderate (mitigation pathway required)" by the
 *      audit screening chip — the same number, opposite readings, on the
 *      same page.
 *
 * So a cluster here is a DETECTOR first and a space-saver second. Members
 * that disagree still merge into one row, but the row is stamped with the
 * disagreement and every position renders with the analyses that hold it.
 *
 * ── The three rules ──────────────────────────────────────────────────
 *
 * R1. NO INVENTED PROSE. A cluster's headline is always the verbatim text
 *     of one of its members. Nothing on this surface is model-written, so
 *     nothing new can be untrue. Every other member survives verbatim in
 *     `members` for the disclosure row.
 *
 * R2. CROSS-REPORT MERGES ON SUBJECT; WITHIN-REPORT MERGES ONLY ON
 *     RESTATEMENT. Two analyses naming the same subject are restating one
 *     claim — that is precisely the complaint. But two findings from ONE
 *     analysis are a distinction the analysis chose to draw ("Unsecured
 *     Water Rights" vs "Dual Water Rights Doctrine"), and flattening them
 *     destroys information. Within a single report we merge only on high
 *     lexical containment, which catches the real case — the same claim
 *     harvested from two different fields of one report.
 *
 * R3. A MISS IS A SINGLETON. Text that matches no subject stays its own
 *     cluster. Guessing that two findings are "sort of about the same
 *     thing" is worse than leaving them apart, so every matcher is
 *     anchored and total misses fall through (same discipline as
 *     `laneItemStructure`).
 *
 * ── Bands ────────────────────────────────────────────────────────────
 *
 * `structuredFindings` carries two populations. Most entries are analysis
 * prose. The first one to three are audit-injected SCREENING chips — the
 * same deterministic fact ("FEMA flood zone · Zone A", "Interconnection
 * queue · 24-48 months") stamped into every report type by
 * `constraintExtractor`. Their 7-source citation run is one fact counted
 * seven times, not seven corroborations, so they are split into a
 * trailing `screening` band and collapsed to a single source. They are
 * detected by the boilerplate detail sentence that `laneItemStructure`
 * already parses — no new heuristic.
 *
 * ── Why not the two existing near-duplicate finders ─────────────────
 *
 * `audit/actionDeduper` clusters near-duplicate recommendations by embedding
 * cosine similarity, intra- and cross-report. It is the right tool for the
 * audit roadmap and the wrong one here: it is ASYNC and needs a model call,
 * while this runs synchronously inside a React render and inside a dossier
 * builder that must stay deterministic and hash-stable. A merge that depends
 * on a network round-trip also cannot be unit-tested against a fixture, and an
 * embedding score gives no answer to the question that actually matters on
 * this surface — WHAT do the two statements disagree about.
 *
 * `findingFingerprint` hashes a finding's first eight significant words,
 * sorted, so the Apply-on-Rerun pack can drop findings the user already chose
 * to ignore. It is explicitly recall-biased ("we'd rather drop a near-duplicate
 * than re-nag the user"), which is the correct trade for an ignore filter and
 * the exact inverse of the trade here: a false collapse in a decision document
 * deletes evidence. It is also a single hash, not a pairwise test, so
 * "Low Regional Water Stress" and the long WRI sentence that restates it
 * produce entirely different keys.
 *
 * Pure module — no React, no store, no I/O. Runs client-side in the
 * dossier and server-side in the Decision Guide builder.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { parseLaneFact } from './laneItemStructure';

export type FindingPolarity = 'positive' | 'negative' | 'neutral';

/** `analysis` = authored by an analysis. `screening` = audit-injected chip. */
export type FindingBand = 'analysis' | 'screening';

export interface FindingSourceRef {
  id: string;
  type: ReportTypeId;
  label: string;
  versionLabel: string;
}

export interface FindingCandidate {
  title: string;
  detail?: string;
  /** Raw severity/impact as persisted — may be an enum OR a prose sentence. */
  severity?: string;
  polarity?: FindingPolarity;
  /**
   * Whether `title` is a heading the analysis authored (`contextualKeyFindings
   * [].title`) or a prose sentence lifted from a conclusion array
   * (`conclusion.topStrengths[]`). Only the caller knows which field it read,
   * and the distinction decides which member wins the cluster headline —
   * ranking on length alone always picked the sentence.
   */
  titleKind?: 'headline' | 'statement';
  source: FindingSourceRef;
}

/** A quantity the subject's probe isolated as the one the claim is ABOUT. */
export interface ClusterQuantity {
  value: number;
  unit: string;
  /** Verbatim slice the probe matched, for the disclosure row. */
  text: string;
}

export interface ClusterMember {
  title: string;
  titleKind: 'headline' | 'statement';
  detail?: string;
  /** Severity reduced to a renderable tag, or undefined when it was prose. */
  severity?: string;
  polarity: FindingPolarity;
  source: FindingSourceRef;
  quantity: ClusterQuantity | null;
}

export interface DisagreementPosition {
  /** The position as stated — a number+unit, or a polarity word. */
  value: string;
  sources: FindingSourceRef[];
}

export interface ClusterDisagreement {
  kind: 'quantity' | 'polarity';
  /**
   * Reader-facing statement of WHAT disagrees, composed only from member
   * values and source labels — no judgement about which is right.
   */
  summary: string;
  positions: DisagreementPosition[];
}

export interface FindingCluster {
  key: string;
  /**
   * True when the members' TITLES differ, so a disclosure list tells the
   * reader something. The disclosure renders titles, so this compares titles —
   * six rows reading "Analysis is held for quality review" say nothing the
   * source count has not already said, however much their details vary.
   */
  membersDiffer: boolean;
  subjectId: string | null;
  subjectLabel: string | null;
  band: FindingBand;
  /** Verbatim title of the richest member. Never synthesised (R1). */
  headline: string;
  headlineSourceId: string;
  detail?: string;
  severity?: string;
  polarity: FindingPolarity;
  /** Every contributing statement, verbatim, headline member first. */
  members: ClusterMember[];
  /** Distinct contributing analyses. One entry per report id. */
  sources: FindingSourceRef[];
  disagreement: ClusterDisagreement | null;
}

/**
 * A subject that appears with opposite polarity in two different bands —
 * today only the screening-chip-vs-analysis case (WRI 1.53/5 read as
 * "Moderate · mitigation required" by the chip and "Low · favorable" by
 * the water analysis). Surfaced so the screening row can say so rather
 * than sitting silently next to its own contradiction.
 */
export interface CrossBandContradiction {
  subjectId: string;
  subjectLabel: string;
  summary: string;
  positions: DisagreementPosition[];
}

/* ───────────────────────── severity normalisation ───────────────────── */

/**
 * `structuredFindings[].impact` is typed `string` and is genuinely
 * polysemous in persisted reports: sometimes the enum (`"negative"`),
 * sometimes a full impact paragraph ("High. Significantly constrains site
 * layout, increases construction costs for fill and foundation work, …").
 * It is rendered as a 10px small-caps tag, so on this site 15 of 47
 * entries set a 200-character paragraph as a severity chip.
 *
 * Recover the leading grade word when there is one, keep genuinely short
 * values as-is, and drop the rest rather than render a paragraph as a tag.
 */
const SEVERITY_GRADE_RE =
  /^\s*(critical|very high|medium-high|medium high|high|moderate|medium|low|none)\b/i;

/** Polarity words are carried by `polarity`; as a severity TAG they say nothing. */
const POLARITY_WORDS = new Set(['negative', 'positive', 'neutral']);

const SEVERITY_TAG_MAX = 24;

export function normalizeSeverityTag(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (POLARITY_WORDS.has(trimmed.toLowerCase())) return undefined;
  const grade = SEVERITY_GRADE_RE.exec(trimmed);
  if (grade) return grade[1].toLowerCase();
  return trimmed.length <= SEVERITY_TAG_MAX ? trimmed : undefined;
}

/**
 * Polarity is read ONLY from the explicit enum values. A severity GRADE is
 * not a polarity: "High. The on-site pipeline access dramatically improves
 * the feasibility of the BTM gas generation strategy" is a high-impact
 * POSITIVE finding, and inferring `negative` from the word "High" would
 * have moved a strength into the risk register.
 */
const POLARITY_LEAD_RE = /^\s*(positive|negative|neutral)\b/i;

export function polarityFromImpact(raw: unknown): FindingPolarity {
  if (typeof raw !== 'string') return 'neutral';
  // Matches the bare enum AND the leading word of an impact SENTENCE
  // ("Positive. This competitive rate improves the site's operating cost
  // profile…"). It deliberately does not read grade words: "High." and
  // "Critical." lead both favourable and unfavourable impact sentences.
  const lead = POLARITY_LEAD_RE.exec(raw.trim());
  if (!lead) return 'neutral';
  return lead[1].toLowerCase() as FindingPolarity;
}

/**
 * Polarity for a candidate, falling back to the LANE the caller is filling.
 *
 * The lane is real evidence, not a guess: `conclusion.topStrengths[]` is a
 * favourable field by definition and `topRisks[]`/`topConstraints[]` are
 * unfavourable by definition, so an entry read out of one of them carries
 * its lane's polarity even when it has no impact field of its own. Without
 * this, everything without an explicit enum reads `neutral` and the
 * favourable/unfavourable contradiction detector never fires.
 */
export function resolvePolarity(
  impact: unknown,
  laneDefault: FindingPolarity,
): FindingPolarity {
  const explicit = polarityFromImpact(impact);
  return explicit === 'neutral' ? laneDefault : explicit;
}

/* ─────────────────────────── subject vocabulary ─────────────────────── */

interface QuantityProbe {
  unit: string;
  /**
   * Ordered alternatives. The FIRST pattern that hits, scanning the text
   * left to right, wins — so "Critical 500 MW Power Deficit Above
   * Unverified 50 MW Capacity Indication" resolves to 500, not 50. Every
   * window is `[^.]` bounded so a probe can never reach across a sentence
   * boundary and pair a number with someone else's noun.
   */
  patterns: RegExp[];
}

interface ClaimSubject {
  id: string;
  label: string;
  /** Every entry must hit somewhere in the TITLE. */
  match: RegExp[];
  /** Any hit disqualifies the subject. */
  veto?: RegExp[];
  /** Isolates the quantity the claim is about, over title + detail. */
  quantity?: QuantityProbe;
}

/**
 * DOMAIN VOCABULARY — evaluated in order, FIRST MATCH WINS.
 *
 * Order is load-bearing where titles carry more than one domain noun:
 * `easements` precedes `gas-pipeline` so "Multiple Pipeline Easements
 * Constrain Site Layout" is an easement finding, not a pipeline one;
 * `water-stress` precedes `water-rights` so the WRI-score findings do not
 * land in the permitting cluster.
 *
 * Matched on TITLE ONLY. Details run long and drag unrelated domain nouns
 * in with them — matching "Significant Permitting Risk from 65.7 Acres of
 * On-Site Wetlands" on its detail would put it in the permitting cluster
 * because the detail mentions a Section 404 permit.
 *
 * EXTENSION POINT: adding a subject is a table row. A subject that is
 * absent simply means those findings stay as singletons (R3) — the failure
 * mode of an omission is the status quo, never a wrong merge.
 */
const CLAIM_SUBJECTS: readonly ClaimSubject[] = [
  {
    id: 'grid-capacity',
    label: 'Grid capacity',
    match: [/\b(deficit|shortfall)\b|\bcapacity\s+(gap|shortfall)\b|\bcapacity\b(?=.*\b(grid|power|mw)\b)/i],
    veto: [/\bqueue\b/i],
    quantity: {
      unit: 'MW',
      patterns: [
        /(\d[\d,]*(?:\.\d+)?)\s*MW\b[^.]{0,30}?\b(?:deficit|shortfall|gap)\b/i,
        /\b(?:deficit|shortfall|gap)\b[^.]{0,30}?\b(\d[\d,]*(?:\.\d+)?)\s*MW\b/i,
      ],
    },
  },
  { id: 'interconnection-queue', label: 'Interconnection queue', match: [/\bqueue\b|\benergi[sz]ation\b/i] },
  {
    id: 'interconnection-voltage',
    label: 'Interconnection voltage',
    match: [/\bvoltage\b|\b\d{3}\s?kv\b/i, /\bconflict|\bdiscrepan|\bambigu/i],
  },
  { id: 'iso-rto', label: 'ISO / RTO designation', match: [/\biso\b|\brto\b|\bmiso\b/i] },
  { id: 'easements', label: 'Easements', match: [/\beasement/i] },
  {
    id: 'floodplain',
    label: 'Floodplain',
    match: [/\bflood/i],
    quantity: {
      unit: '%',
      patterns: [
        /(\d[\d,]*(?:\.\d+)?)\s*%[^.]{0,40}?\b(?:flood|sfha|site area)\b/i,
        /\b(?:flood|floodplain|sfha)\b[^.]{0,40}?\((\d[\d,]*(?:\.\d+)?)\s*%/i,
      ],
    },
  },
  {
    id: 'wetlands',
    label: 'Wetlands',
    match: [/\bwetland/i],
    quantity: {
      unit: 'acres',
      patterns: [
        /(\d[\d,]*(?:\.\d+)?)\s*acres?[^.]{0,30}?\bwetland/i,
        /\bwetlands?\b[^.]{0,30}?\b(\d[\d,]*(?:\.\d+)?)\s*acres?\b/i,
      ],
    },
  },
  { id: 'rezoning', label: 'Rezoning and entitlement', match: [/\brezon|\bzoning\b|\bentitlement/i] },
  { id: 'water-stress', label: 'Water stress', match: [/\bwater stress\b|\baqueduct\b/i] },
  { id: 'water-rights', label: 'Water rights', match: [/\bwater rights?\b|\bwithdrawal permit|\briparian\b/i] },
  { id: 'air-quality', label: 'Air quality', match: [/\bair quality\b|\battainment\b/i] },
  {
    id: 'permitting-path',
    label: 'Permitting path',
    // Both halves required. `permitting` alone is a word that appears in
    // findings ABOUT permitting and in findings that merely mention it in
    // passing; only the second group names the path itself.
    match: [/\bpermitting\b/i, /\bcomplex|\blengthy\b|\bpath\b|\btimeline\b|\bmulti-?agency\b/i],
  },
  { id: 'tribal-consultation', label: 'Tribal consultation', match: [/\btribal\b|\bsection 106\b|\bnhpa\b/i] },
  { id: 'gas-pipeline', label: 'Gas pipeline access', match: [/\bpipeline\b/i, /\bgas\b|\bbtm\b|\bgeneration\b/i] },
  { id: 'network-latency', label: 'Network latency', match: [/\blatency\b/i] },
  { id: 'seismic', label: 'Seismic risk', match: [/\bseismic\b/i] },
  { id: 'topography', label: 'Topography', match: [/\bslope\b|\btopograph/i] },
  { id: 'road-access', label: 'Road access', match: [/\broad (access|proximity)\b/i] },
  { id: 'electricity-rates', label: 'Electricity rates', match: [/\belectricity rate|\bindustrial rate/i] },
  { id: 'onsite-renewables', label: 'On-site renewables', match: [/\bwind\b|\bsolar\b/i] },
  { id: 'capex', label: 'CAPEX', match: [/\bcapex\b/i] },
  { id: 'long-lead', label: 'Long-lead equipment', match: [/\blong.lead\b|\blead time\b/i] },
] as const;

function subjectFor(title: string): ClaimSubject | null {
  for (const subject of CLAIM_SUBJECTS) {
    if (subject.veto?.some(re => re.test(title))) continue;
    if (subject.match.every(re => re.test(title))) return subject;
  }
  return null;
}

/* ────────────────────────── quantity extraction ─────────────────────── */

function probeQuantity(probe: QuantityProbe | undefined, text: string): ClusterQuantity | null {
  if (!probe) return null;
  let best: { index: number; value: number; text: string } | null = null;
  for (const pattern of probe.patterns) {
    const hit = pattern.exec(text);
    if (!hit) continue;
    const raw = hit[1];
    const value = Number(raw.replace(/,/g, ''));
    if (!Number.isFinite(value)) continue;
    if (!best || hit.index < best.index) {
      best = { index: hit.index, value, text: hit[0].trim() };
    }
  }
  if (!best) return null;
  return { value: best.value, unit: probe.unit, text: best.text };
}

/* ───────────────────────── restatement containment ──────────────────── */

const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'for', 'to', 'in', 'on', 'at', 'by',
  'with', 'from', 'is', 'are', 'was', 'were', 'be', 'as', 'that', 'this',
  'these', 'those', 'its', 'it', 'has', 'have', 'but', 'not', 'per', 'into',
  'requires', 'require', 'required', 'requiring',
]);

function contentTokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const raw of text.toLowerCase().split(/[^a-z0-9.]+/)) {
    const token = raw.replace(/^\.+|\.+$/g, '');
    if (!token) continue;
    if (STOPWORDS.has(token)) continue;
    if (token.length < 3 && !/\d/.test(token)) continue;
    // Crude suffix fold so "enables"/"enabling" and "wetland"/"wetlands"
    // count as the same token. Deliberately not a stemmer — this only has
    // to decide "is B a restatement of A", never to index anything.
    out.add(token.replace(/(?:ing|ed|es|s)$/, ''));
  }
  return out;
}

/** Shared content tokens, and |A ∩ B| / min(|A|,|B|). */
function overlap(a: Set<string>, b: Set<string>): { shared: number; containment: number } {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  if (small.size === 0) return { shared: 0, containment: 0 };
  let shared = 0;
  for (const token of small) if (large.has(token)) shared += 1;
  return { shared, containment: shared / small.size };
}

/**
 * Is `b` the same statement as `a`, reworded?
 *
 * TWO conditions, and the absolute one is load-bearing. A ratio alone is
 * unstable on short text: "Alpha claim here" and "Beta claim here" are three
 * content tokens each sharing two, which is 0.67 containment — a merge, on two
 * generic words. Requiring real overlap AS WELL AS proportional overlap also
 * stops a short label being swallowed by a longer claim that repeats it
 * ("Environmental" shares one token with "Environmental review pending").
 */
const RESTATEMENT_MIN_SHARED = 3;

function isRestatement(a: Set<string>, b: Set<string>): boolean {
  const { shared, containment } = overlap(a, b);
  return shared >= RESTATEMENT_MIN_SHARED && containment >= RESTATEMENT_CONTAINMENT;
}

/**
 * How completely one text must sit inside another before two findings from
 * the SAME analysis are treated as one restatement (R2). Tuned against live
 * data: "Low Regional Water Stress" sits 1.00 inside "Low baseline water
 * stress (WRI Aqueduct score of 1.53/5) and high regional rainfall create a
 * favorable resource environment" (merge, one claim in two fields), while
 * "Unsecured Water Rights for Cooling Demand" sits only 0.40 inside "Dual
 * Water Rights Doctrine Requires Specific Permitting Strategy" (keep apart,
 * two claims the analysis chose to distinguish).
 */
const RESTATEMENT_CONTAINMENT = 0.6;

/* ──────────────────────────────── clustering ────────────────────────── */

interface WorkingMember extends ClusterMember {
  tokens: Set<string>;
  /** Content tokens of the TITLE alone — decides label-vs-claim. */
  titleTokens: Set<string>;
  /** The subject quantity appears in the TITLE, not only in the detail. */
  quantityInTitle: boolean;
  /**
   * The screening chip's own value ("12-24 months", "Zone A", "Moderate"),
   * recovered from the boilerplate detail. It is a screening row's ONLY
   * varying content — its title is a bare label — so it is the axis a
   * screening cluster can disagree on.
   */
  screeningValue: string | null;
}

interface WorkingCluster {
  subject: ClaimSubject | null;
  band: FindingBand;
  members: WorkingMember[];
}

function bandFor(parsed: ReturnType<typeof parseLaneFact>): FindingBand {
  return parsed?.fromBoilerplate ? 'screening' : 'analysis';
}

function normalizedKey(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

/**
 * Richest member wins the headline (R1). A member that names the subject's
 * quantity outranks one that does not — "Critical 500 MW Power Deficit"
 * says more than "Significant Power Deficit Between Project Requirement and
 * Indicative Grid Capacity" despite being shorter. Ties break on title
 * length, then on source id, so the output is fully deterministic.
 */
/** A title with fewer content tokens than this is a LABEL, not a claim. */
const HEADLINE_MIN_TOKENS = 4;

/** Does `a` say everything `b` says, and more? */
function subsumes(a: Set<string>, b: Set<string>): boolean {
  if (a.size <= b.size) return false;
  for (const token of b) if (!a.has(token)) return false;
  return true;
}

/**
 * Which member's wording heads a merged row.
 *
 * The row shows ONE member verbatim (R1), so this decides what the reader sees
 * and what is demoted into the disclosure. Two failure modes bound the choice,
 * and both were observed on live data:
 *
 *   - a thin LABEL winning and burying the content ("CAPEX Variance" over the
 *     sentence explaining the $80M exposure; "Section 404 jurisdictional
 *     wetlands" over the same words plus ON THE EASTERN THIRD);
 *   - a PARAGRAPH winning and destroying the row as a headline ("Lead Time:
 *     Lead times for critical path items such as large power transformers and
 *     500 kV switchgear are unknown and not included in the project schedule…"
 *     over "Fuel-Supply Long-Lead Dependency").
 *
 * Neither "prefer the heading" nor "prefer the most content" avoids both. What
 * separates them is SUBSUMPTION: when one title says everything another says
 * and adds to it, the fuller one is strictly better and wins — that is the
 * wetlands and CAPEX case. When the two titles simply use different words,
 * neither subsumes, and the analysis's own heading wins — that is the
 * long-lead and next-action case, where the terse heading is the better row.
 *
 * Rank order:
 *   1-2. Names the subject's quantity — in the title, then anywhere.
 *   3.   Subsumes another member's title (strictly more of the same claim).
 *   4.   Is the analysis's own heading, and is a claim not a label.
 *   5-6. Title content tokens, then length, so the order is total.
 */
function richness(member: WorkingMember, all: readonly WorkingMember[]): number[] {
  // Only subsuming another CLAIM counts. Subsuming a bare label proves
  // nothing — every paragraph contains some two-word label, and live that let
  // "Lead Time: Lead times for critical path items such as large power
  // transformers and 500 kV switchgear are unknown…" win a row by swallowing
  // the label "Lead Time", beating the far better headline it was actually
  // competing with, "Fuel-Supply Long-Lead Dependency".
  const subsumesAnother = all.some(other => (
    other !== member
    && other.titleTokens.size >= HEADLINE_MIN_TOKENS
    && subsumes(member.titleTokens, other.titleTokens)
  ));
  return [
    member.quantityInTitle ? 1 : 0,
    member.quantity ? 1 : 0,
    subsumesAnother ? 1 : 0,
    member.titleKind === 'headline' && member.titleTokens.size >= HEADLINE_MIN_TOKENS ? 1 : 0,
    member.titleTokens.size,
    member.title.length,
  ];
}

function pickHeadline(members: readonly WorkingMember[]): WorkingMember {
  return [...members].sort((left, right) => {
    const l = richness(left, members);
    const r = richness(right, members);
    for (let i = 0; i < l.length; i += 1) {
      if (l[i] !== r[i]) return r[i] - l[i];
    }
    // Total order — never leave the headline to Array.sort stability.
    return left.source.id.localeCompare(right.source.id);
  })[0];
}

function positionsFrom(
  members: readonly WorkingMember[],
  valueOf: (member: WorkingMember) => string | null,
): DisagreementPosition[] {
  const byValue = new Map<string, DisagreementPosition>();
  for (const member of members) {
    const value = valueOf(member);
    if (value == null) continue;
    const entry = byValue.get(value) ?? { value, sources: [] };
    if (!entry.sources.some(s => s.id === member.source.id)) entry.sources.push(member.source);
    byValue.set(value, entry);
  }
  return [...byValue.values()];
}

function formatQuantity(quantity: ClusterQuantity): string {
  return `${quantity.value} ${quantity.unit}`;
}

function sourceNames(sources: readonly FindingSourceRef[]): string {
  return sources.map(s => s.label).join(', ');
}

function detectDisagreement(
  subject: ClaimSubject | null,
  members: readonly WorkingMember[],
): ClusterDisagreement | null {
  if (members.length < 2) return null;

  // Quantity first — a numeric split is the more consequential of the two.
  const quantityPositions = positionsFrom(members, m =>
    m.quantity ? formatQuantity(m.quantity) : null);
  if (quantityPositions.length > 1) {
    const ordered = [...quantityPositions].sort(
      (l, r) => r.sources.length - l.sources.length || l.value.localeCompare(r.value),
    );
    return {
      kind: 'quantity',
      summary: `Analyses disagree on ${subject?.label.toLocaleLowerCase() ?? 'this value'}: `
        + ordered.map(p => `${p.value} (${sourceNames(p.sources)})`).join(' vs '),
      positions: ordered,
    };
  }

  const screeningPositions = positionsFrom(members, m => m.screeningValue);
  if (screeningPositions.length > 1) {
    const ordered = [...screeningPositions].sort(
      (l, r) => r.sources.length - l.sources.length || l.value.localeCompare(r.value),
    );
    return {
      kind: 'quantity',
      summary: `Analyses carry different screening values for `
        + `${subject?.label.toLocaleLowerCase() ?? 'this check'}: `
        + ordered.map(p => `${p.value} (${sourceNames(p.sources)})`).join(' vs '),
      positions: ordered,
    };
  }

  const polarityPositions = positionsFrom(members, m =>
    m.polarity === 'neutral' ? null : m.polarity);
  if (polarityPositions.length > 1) {
    const ordered = [...polarityPositions].sort(
      (l, r) => r.sources.length - l.sources.length || l.value.localeCompare(r.value),
    );
    return {
      kind: 'polarity',
      summary: `Analyses disagree on whether this is favourable: `
        + ordered.map(p => `${p.value} (${sourceNames(p.sources)})`).join(' vs '),
      positions: ordered,
    };
  }

  return null;
}

/** Majority polarity, with `neutral` as the fallback and the tiebreak. */
function clusterPolarity(members: readonly WorkingMember[]): FindingPolarity {
  let positive = 0;
  let negative = 0;
  for (const member of members) {
    if (member.polarity === 'positive') positive += 1;
    if (member.polarity === 'negative') negative += 1;
  }
  if (negative > positive) return 'negative';
  if (positive > negative) return 'positive';
  return 'neutral';
}

/**
 * Cluster a lane's candidates.
 *
 * Input order is preserved: clusters come out in the order their first
 * member arrived, with the `screening` band moved to the end. Ordering is
 * never a function of member count — see the band note above for why a
 * high source count on this surface is not evidence of agreement.
 */
export function clusterFindings(
  candidates: readonly FindingCandidate[],
): FindingCluster[] {
  const working: WorkingCluster[] = [];
  const bySubject = new Map<string, WorkingCluster[]>();
  const byExactTitle = new Map<string, WorkingCluster>();

  for (const candidate of candidates) {
    const title = candidate.title.trim();
    if (!title) continue;
    const detail = candidate.detail?.trim() || undefined;
    const parsed = parseLaneFact(title, detail);
    const band = bandFor(parsed);
    // A scored metric ("Flood Risk: Flood risk: High (20/100)") renders in the
    // lane's Metric|Value|Score TABLE, not as narrative. Clustering it into a
    // prose row would drop its score into a disclosure list, so scored items
    // are never subject-clustered — they stay exactly as they render today.
    const subject = parsed?.score != null ? null : subjectFor(title);
    const severityTag = normalizeSeverityTag(candidate.severity);
    const member: WorkingMember = {
      title,
      titleKind: candidate.titleKind ?? 'statement',
      ...(detail ? { detail } : {}),
      ...(severityTag ? { severity: severityTag } : {}),
      polarity: candidate.polarity ?? 'neutral',
      source: candidate.source,
      quantity: probeQuantity(subject?.quantity, `${title}. ${detail ?? ''}`),
      quantityInTitle: probeQuantity(subject?.quantity, title) != null,
      screeningValue: parsed?.fromBoilerplate ? parsed.value : null,
      tokens: contentTokens(`${title} ${detail ?? ''}`),
      titleTokens: contentTokens(title),
    };

    /*
     * Exact-title equality merges — but NOT unconditionally.
     *
     * Live on Redhead: five analyses each emitted a condition titled
     * "Condition 1", "Condition 2", … Those are PLACEHOLDERS; the actual
     * condition is in the detail, and it differs in every one. Merging on the
     * title alone collapsed five distinct conditions into a single row called
     * "Condition 1" and dropped four of them from view.
     *
     * So identical titles merge only when the title carries enough substance
     * to identify a claim on its own, OR when the details agree too (the
     * screening chips, where every analysis is genuinely stamped with the same
     * fact). A thin, repeated title over differing details is a numbering
     * scheme, not a shared claim.
     */
    const exactKey = `${band}:${normalizedKey(title)}`;
    const exact = byExactTitle.get(exactKey);
    if (exact) {
      const titleIdentifies = contentTokens(title).size >= HEADLINE_MIN_TOKENS;
      const detailsAgree = exact.members.some(
        m => normalizedKey(m.detail ?? '') === normalizedKey(detail ?? ''),
      );
      if (titleIdentifies || detailsAgree) {
        exact.members.push(member);
        continue;
      }
    }

    let target: WorkingCluster | undefined;
    if (subject) {
      const candidatesForSubject = bySubject.get(`${band}:${subject.id}`) ?? [];
      target = candidatesForSubject.find((cluster) => {
        // R2 — the check is against this report's OWN members in the cluster,
        // never against the cluster as a whole: once report B has joined a
        // cluster seeded by report A, a `cluster.members.every(...)` test would
        // wave through A's SECOND finding and flatten the distinction A drew.
        const ownMembers = cluster.members.filter(m => m.source.id === member.source.id);
        if (ownMembers.length === 0) return true; // cross-report merges on subject alone
        return ownMembers.some(m => isRestatement(m.tokens, member.tokens));
      });
      if (!target) {
        target = { subject, band, members: [] };
        working.push(target);
        candidatesForSubject.push(target);
        bySubject.set(`${band}:${subject.id}`, candidatesForSubject);
      }
    } else {
      target = { subject: null, band, members: [] };
      working.push(target);
    }

    target.members.push(member);
    byExactTitle.set(exactKey, target);
  }

  const analysisBand = working.filter(cluster => cluster.band === 'analysis');
  const screeningBand = working.filter(cluster => cluster.band === 'screening');
  const ordered = [...analysisBand, ...screeningBand];

  const seenSubjectKeys = new Map<string, number>();
  const clusters = ordered.map((cluster): FindingCluster => {
    // One analysis restating itself across two persisted fields
    // (`structuredFindings` mirrors `contextualKeyFindings` verbatim in every
    // live report) is ONE statement. Collapse FIRST, so the majority polarity
    // vote and the disagreement scan both see one vote per analysis per claim
    // rather than one per persisted field. The first occurrence wins, which is
    // the richer field: callers read `structuredFindings` — the one carrying
    // the impact sentence — before the contextual mirror.
    const distinct: WorkingMember[] = [];
    const seenStatements = new Set<string>();
    for (const member of cluster.members) {
      const statementKey = `${member.source.id}:${normalizedKey(member.title)}`;
      if (seenStatements.has(statementKey)) continue;
      seenStatements.add(statementKey);
      distinct.push(member);
    }

    const headline = pickHeadline(distinct);
    const sources: FindingSourceRef[] = [];
    for (const member of distinct) {
      if (!sources.some(s => s.id === member.source.id)) sources.push(member.source);
    }
    const subjectId = cluster.subject?.id ?? null;
    const baseKey = subjectId
      ? `${cluster.band}:${subjectId}`
      : `${cluster.band}:solo:${normalizedKey(headline.title)}`;
    const seen = seenSubjectKeys.get(baseKey) ?? 0;
    seenSubjectKeys.set(baseKey, seen + 1);

    const members: ClusterMember[] = [
      headline,
      ...distinct.filter(member => member !== headline),
    ].map(({
      tokens: _tokens,
      titleTokens: _titleTokens,
      quantityInTitle: _inTitle,
      screeningValue: _screeningValue,
      ...rest
    }) => rest);

    return {
      key: seen === 0 ? baseKey : `${baseKey}#${seen}`,
      membersDiffer: new Set(distinct.map(m => normalizedKey(m.title))).size > 1,
      subjectId,
      subjectLabel: cluster.subject?.label ?? null,
      band: cluster.band,
      headline: headline.title,
      headlineSourceId: headline.source.id,
      ...(headline.detail ? { detail: headline.detail } : {}),
      ...(headline.severity ? { severity: headline.severity } : {}),
      polarity: clusterPolarity(distinct),
      members,
      sources,
      disagreement: detectDisagreement(cluster.subject, distinct),
    };
  });

  return clusters;
}

/**
 * Collapse restatements inside ONE author's own text.
 *
 * This is the WITHIN-AUTHOR half of R2, for the conclusion builders. They
 * concatenate a report's own fields — the model's `conclusion.topStrengths[]`
 * with the same report's `keyFindings[]` — and deduplicated by exact string
 * equality, so a claim the model wrote into two different fields printed twice
 * in the generated document.
 *
 * Subject matching is deliberately NOT required here. Cross-report merging
 * needs a subject because two analyses reach one claim in different words; an
 * author restating itself reuses its own words, so containment is both the
 * stronger signal and the more conservative one — it cannot merge two claims
 * that share only a topic.
 *
 * The survivor is always one of the inputs VERBATIM (R1), the richest of the
 * group, so "Low Regional Water Stress" yields to "Low baseline water stress
 * (WRI Aqueduct score of 1.53/5) and high regional rainfall create a favorable
 * resource environment." Output order follows first appearance, so a caller
 * that slices to a top-N gets N DISTINCT entries instead of N possibly
 * duplicated ones.
 *
 * Blank and whitespace-only entries are dropped, matching the exact-dedupe
 * helpers this replaces.
 */
export function mergeRestatements(values: readonly string[]): string[] {
  const groups: { winner: string; members: Set<string>[]; keys: Set<string> }[] = [];
  for (const raw of values) {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) continue;
    const key = normalizedKey(text);
    const tokens = contentTokens(text);
    const existing = groups.find(group => (
      group.keys.has(key) || group.members.some(member => isRestatement(member, tokens))
    ));
    if (!existing) {
      groups.push({ winner: text, members: [tokens], keys: new Set([key]) });
      continue;
    }
    existing.members.push(tokens);
    existing.keys.add(key);
    // Richest wins, verbatim — never a blend of the two.
    if (text.length > existing.winner.length) existing.winner = text;
  }
  return groups.map(group => group.winner);
}

/**
 * A SCREENING chip whose reading contradicts what the analyses say about the
 * same subject.
 *
 * Live: `water-stress` is stamped into every report by the audit screening
 * pass as "Moderate (mitigation pathway required)" — unfavourable — while the
 * water and environmental analyses read the SAME WRI Aqueduct 1.53/5 as "Low
 * … favorable resource environment". The two sit in different lanes, so no
 * single cluster ever sees the disagreement and the page states both.
 *
 * DELIBERATELY NARROW. An earlier form compared every subject across every
 * lane and produced mostly false positives: an analysis calling out a
 * long-lead FUEL dependency while another notes a long-lead TRANSFORMER has
 * schedule slack is two aspects of one subject, not a contradiction. A
 * screening chip is a single deterministic fact with one reading, so
 * screening-vs-analysis is the one pairing where opposite polarity really does
 * mean the surface is contradicting itself.
 */
export function detectScreeningContradictions(
  laneClusters: readonly (readonly FindingCluster[])[],
): CrossBandContradiction[] {
  const bySubject = new Map<string, FindingCluster[]>();
  for (const lane of laneClusters) {
    for (const cluster of lane) {
      if (!cluster.subjectId || cluster.polarity === 'neutral') continue;
      const list = bySubject.get(cluster.subjectId) ?? [];
      list.push(cluster);
      bySubject.set(cluster.subjectId, list);
    }
  }

  const out: CrossBandContradiction[] = [];
  for (const [subjectId, clusters] of bySubject) {
    const screening = clusters.filter(c => c.band === 'screening');
    const analysis = clusters.filter(c => c.band === 'analysis');
    if (screening.length === 0 || analysis.length === 0) continue;
    const screeningPolarity = screening[0].polarity;
    const opposed = analysis.filter(c => c.polarity !== screeningPolarity);
    if (opposed.length === 0) continue;

    const collect = (group: readonly FindingCluster[]): FindingSourceRef[] => {
      const sources: FindingSourceRef[] = [];
      for (const cluster of group) {
        for (const source of cluster.sources) {
          if (!sources.some(s => s.id === source.id)) sources.push(source);
        }
      }
      return sources;
    };

    const label = analysis[0].subjectLabel ?? screening[0].subjectLabel ?? subjectId;
    const positions: DisagreementPosition[] = [
      { value: `${opposed[0].polarity} (analyses)`, sources: collect(opposed) },
      { value: `${screeningPolarity} (screening)`, sources: collect(screening) },
    ];
    out.push({
      subjectId,
      subjectLabel: label,
      summary: `${label}: the analyses read this as ${opposed[0].polarity} `
        + `(${sourceNames(positions[0].sources)}) while the screening pass reads it as `
        + `${screeningPolarity}. Both readings are shown; neither has been reconciled.`,
      positions,
    });
  }
  return out;
}
